import type { CommunicationsView } from 'src/modules/shared/types/communications-view.type';

/**
 * Auto-selected views are limited to the primary cards/pipeline layouts.
 */
export type AutoCommunicationsView = Extract<
  CommunicationsView,
  'cards' | 'pipeline'
>;

/**
 * Media query used to treat the viewport as a mobile layout.
 */
export const COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY = '(max-width: 1023px)';
/**
 * Media query used to enable split chat/details layouts.
 */
export const COMMUNICATIONS_SPLIT_VIEW_MEDIA_QUERY = '(min-width: 1024px)';
/**
 * Media query used to enable the wide three-panel communications layout.
 */
export const COMMUNICATIONS_WIDE_SPLIT_VIEW_MEDIA_QUERY = '(min-width: 1280px)';

/**
 * Resolves the auto-default Communications view for the current viewport.
 *
 * @returns `cards` on small screens, otherwise `pipeline`.
 */
export const resolveAutoCommunicationsView = (): AutoCommunicationsView => {
  if (typeof window === 'undefined') {
    return 'pipeline';
  }

  return window.matchMedia(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY).matches
    ? 'cards'
    : 'pipeline';
};

/**
 * Resolves the effective Communications view, falling back to auto when absent.
 *
 * @param view - Explicit route/search-selected view.
 * @returns Explicit view or auto-resolved view when missing.
 */
export const resolveCommunicationsView = (
  view?: CommunicationsView
): CommunicationsView => view ?? resolveAutoCommunicationsView();
